import { type FirebaseApp, initializeApp } from "firebase/app";
import {
	createUserWithEmailAndPassword,
	getAuth,
	onAuthStateChanged,
	signInWithCustomToken,
	signInWithEmailAndPassword,
	signOut,
	type User,
} from "firebase/auth";

const firebaseConfigBase64 = import.meta.env
	.VITE_FIREBASE_CONFIG_BASE64 as string;

if (!firebaseConfigBase64) {
	throw new Error(
		"VITE_FIREBASE_CONFIG_BASE64 environment variable is not set"
	);
}

const firebaseConfig = JSON.parse(atob(firebaseConfigBase64));

/**
 * The clients that hold an independent auth session. `/app`, and every other
 * learner-facing route, runs on `app`. `/operations` runs on `operations`, and
 * `/organization` on `organization` (TECH-3200).
 *
 * `/platform` is deliberately NOT one of these. It rides the `/app` session,
 * because platform admin is decided by the `@htctw.org` address an employee is
 * already signed in as. `/organization` is the opposite case. Its audience is a
 * customer's own administrators, who usually also participate in a workspace in
 * the same browser, and one session would make administering their tenant and
 * working in it the same login.
 *
 * Adding a value here is not free. Three places switch on this union and must be
 * extended with it: the named Firebase app below, `detectInitialSurface` in
 * `lib/socket-owner.ts`, and `resetOwnedState` in `stores/create-auth-store.ts`.
 */
export type AuthSurface = "app" | "operations" | "organization";

/** The auth operations of one independent session. */
export type FirebaseSession = {
	surface: AuthSurface;
	login: (email: string, password: string) => Promise<unknown>;
	signup: (email: string, password: string) => Promise<unknown>;
	/**
	 * Sign in with a server-minted custom token, and report which Firebase user
	 * that turned out to be (TECH-3186, the LTI landing route).
	 *
	 * The uid is returned rather than left to be read off `getCurrentUserId`,
	 * because this is the first sign-in path that can run while ANOTHER account is
	 * already signed in on this session. Every other entry point renders a login
	 * form only when there is no user, so "who was here before" was never a
	 * question that could be asked. The caller compares the two and tears down the
	 * previous account's local state. See `lib/lti/launch-flow.ts`.
	 */
	loginWithCustomToken: (token: string) => Promise<string>;
	logout: () => Promise<void>;
	subscribeToAuthChanges: (callback: (user: User | null) => void) => () => void;
	getIdToken: () => Promise<string | null>;
	/**
	 * An ID token together with WHOSE it is, from one snapshot of `currentUser`
	 * (TECH-3247).
	 *
	 * `getIdToken` awaits inside, so a caller that also needs the uid had to read
	 * `getCurrentUserId()` afterwards, and a `signInWithCustomToken` can land in
	 * that window and replace the answer. The socket-identity axis in
	 * `lib/socket-owner.ts` cannot survive that drift. Recording user B against a
	 * socket the server authenticated as A is the state it exists to detect, so a
	 * wrong record there restores the bug it fixed.
	 */
	getIdTokenWithUid: () => Promise<{ idToken: string; uid: string } | null>;
	getCurrentUserId: () => string | null;
};

const createFirebaseSession = (
	surface: AuthSurface,
	app: FirebaseApp
): FirebaseSession => {
	const auth = getAuth(app);

	return {
		surface,
		login: (email, password) =>
			signInWithEmailAndPassword(auth, email, password),
		signup: (email, password) =>
			createUserWithEmailAndPassword(auth, email, password),
		loginWithCustomToken: async (token) => {
			const credential = await signInWithCustomToken(auth, token);
			return credential.user.uid;
		},
		logout: () => signOut(auth),
		subscribeToAuthChanges: (callback) => onAuthStateChanged(auth, callback),
		getIdToken: async () => {
			const user = auth.currentUser;
			if (!user) {
				return null;
			}
			return await user.getIdToken();
		},
		getIdTokenWithUid: async () => {
			// One read of `currentUser`, both values off it, so the uid is the token's
			// owner by construction rather than by timing.
			const user = auth.currentUser;
			if (!user) {
				return null;
			}
			return { idToken: await user.getIdToken(), uid: user.uid };
		},
		getCurrentUserId: () => auth.currentUser?.uid ?? null,
	};
};

// The `/app` session runs on the DEFAULT, unnamed, Firebase app. Keeping it
// unnamed is load-bearing, because Firebase namespaces persisted auth state by
// app name (`firebase:authUser:{apiKey}:{appName}`), so renaming this app would
// sign out every already-logged-in user on deploy.
const appApp = initializeApp(firebaseConfig);

// The operations session runs on a NAMED app pointed at the same Firebase
// project, which is what makes it independent. The name lands in the persistence
// key, so `…:{apiKey}:operations` never collides with `…:{apiKey}:[DEFAULT]`, and
// the cross-tab storage listener is registered against that full namespaced key.
// One user can therefore be signed into `/operations` as an admin and `/app` as
// another account, in two tabs, indefinitely, and signing out of one leaves the
// other untouched. Browser `getAuth` already defaults to a persistent hierarchy
// of indexedDB, then localStorage, then sessionStorage, so no `setPersistence`
// is needed.
const opsApp = initializeApp(firebaseConfig, "operations");

// The organization session, on the same footing and for the same reason
// (TECH-3200). An org administrator usually also participates in a workspace in
// this tenant, so their two roles must be two logins rather than one identity
// that carries administrative reach into `/app`.
const orgApp = initializeApp(firebaseConfig, "organization");

export const appSession = createFirebaseSession("app", appApp);
export const opsSession = createFirebaseSession("operations", opsApp);
export const orgSession = createFirebaseSession("organization", orgApp);

// Back-compat. The `/app` session's operations keep their original module-level
// names, so existing importers are unaffected.
export const login = appSession.login;
export const signup = appSession.signup;
export const logout = appSession.logout;
export const subscribeToAuthChanges = appSession.subscribeToAuthChanges;
export const getIdToken = appSession.getIdToken;
export const getCurrentUserId = appSession.getCurrentUserId;
